import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http, money } from "../lib/api";
import { Placeholder } from "../components/ProductCard";
import InterestForm from "../components/InterestForm";
import { Sparkles, Package, MapPin } from "lucide-react";

export default function ComingSoon() {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    http.get("/products", { params: { coming_soon: true } }).then((r) =>
      setProducts(r.data)
    );
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 py-14">
      <div className="text-center mb-14 space-y-4">
        <span className="oh-badge oh-badge-lime inline-flex items-center gap-2" data-testid="cs-badge">
          <Sparkles size={12}/> Launching Next
        </span>
        <h1 className="font-serif text-5xl sm:text-6xl" data-testid="cs-title">Coming Soon</h1>
        <p className="text-white/70 max-w-2xl mx-auto">
          Two new premium supplements are launching from Organic Healing. Register your interest
          on any product below and we'll send you an <b className="text-lime-400">exclusive early-access offer</b> the
          moment it goes live — no spam, one email at launch.
        </p>
      </div>

      {products.length === 0 && (
        <div className="oh-card p-10 text-center text-white/60" data-testid="cs-empty">
          No coming-soon products right now. Check back shortly.
        </div>
      )}

      <div className="space-y-16">
        {products.map((p, idx) => (
          <article
            key={p.id}
            className={`oh-card overflow-hidden grid lg:grid-cols-2 gap-0 ${idx % 2 === 1 ? "lg:[&>div:first-child]:order-2" : ""}`}
            data-testid={`cs-product-${p.slug}`}>
            <div className="relative bg-[var(--oh-surface)]">
              {p.images && p.images[0] ? (
                <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover aspect-square lg:aspect-auto"/>
              ) : (
                <Placeholder category={p.category}/>
              )}
              <div className="absolute top-4 left-4 oh-badge oh-badge-gold">Coming Soon</div>
              {p.images && p.images[1] && (
                <img
                  src={p.images[1]}
                  alt={`${p.name} nutrition panel`}
                  className="absolute bottom-4 right-4 h-24 w-24 sm:h-32 sm:w-32 object-cover rounded-lg border-2 border-white/20 shadow-2xl"/>
              )}
            </div>

            <div className="p-8 lg:p-12 space-y-5">
              <div className="flex flex-wrap gap-2">
                <span className="oh-badge oh-badge-turq">Supplement</span>
                {p.suitable_for?.map((s) => (
                  <span key={s} className="oh-badge oh-badge-lime">{s}</span>
                ))}
                <span className="oh-badge oh-badge-gold">{p.net_quantity}</span>
              </div>

              <h2 className="font-serif text-3xl sm:text-4xl leading-tight">{p.name}</h2>

              <p className="text-white/75 leading-relaxed">{p.short_description}</p>

              {p.nutrition && (
                <details className="border-l-2 border-lime-500/50 pl-4">
                  <summary className="text-xs font-mono tracking-widest text-lime-400 uppercase cursor-pointer">
                    Nutrition Panel · tap to expand
                  </summary>
                  <p className="text-sm text-white/70 mt-3 leading-relaxed">{p.nutrition}</p>
                </details>
              )}

              {p.ingredients && (
                <div>
                  <h3 className="font-mono text-xs tracking-widest text-teal-400 uppercase mb-1">Ingredients</h3>
                  <p className="text-sm text-white/75">{p.ingredients}</p>
                </div>
              )}

              {p.directions && (
                <div>
                  <h3 className="font-mono text-xs tracking-widest text-teal-400 uppercase mb-1">Directions</h3>
                  <p className="text-sm text-white/75">{p.directions}</p>
                </div>
              )}

              <div className="text-[11px] font-mono text-white/50 tracking-widest flex items-center gap-4 flex-wrap">
                <span className="flex items-center gap-1.5"><Package size={12}/> {p.net_quantity}</span>
                <span className="flex items-center gap-1.5"><MapPin size={12}/> {p.country_of_origin || "United Kingdom"}</span>
              </div>

              <div className="oh-card p-5 border-lime-500/40 !bg-lime-500/5">
                <InterestForm product={p}/>
              </div>

              <div className="flex gap-3 pt-2">
                <Link
                  to={`/product/${p.slug}`}
                  data-testid={`cs-view-${p.slug}`}
                  className="oh-btn-ghost px-5 py-2.5 rounded-full text-xs tracking-widest uppercase">
                  Full Product Details →
                </Link>
              </div>
            </div>
          </article>
        ))}
      </div>

      <div className="mt-20 oh-card p-10 rounded-3xl text-center bg-gradient-to-br from-[#0F2E36] to-[#0B2228]">
        <span className="oh-badge oh-badge-turq">Shop Live Range</span>
        <h2 className="font-serif text-4xl mt-3 mb-3">While You Wait…</h2>
        <p className="text-white/70 max-w-xl mx-auto text-sm mb-6">
          Explore our full range of honey, natural supplements and traditional wellness products —
          currently 10% off across the board.
        </p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link to="/shop" className="oh-btn-lime px-6 py-2.5 rounded-full text-xs tracking-widest uppercase">Shop All Products</Link>
          <Link to="/supplements" className="oh-btn-ghost px-6 py-2.5 rounded-full text-xs tracking-widest uppercase">Browse Supplements</Link>
        </div>
      </div>
    </div>
  );
}
